// e-Jagriti (Consumer Court) backend API helpers.
//
// Talks to the `court-data-service` Cloud Run app (ValscoTech/E-jagrithi), which
// scrapes the e-Jagriti consumer-court portal. The server handles captcha and
// sessions internally — the client just sends plain JSON.
//
// Override the base URL for local dev with REACT_APP_EJAGRITI_API_URL.

export const EJAGRITI_API_BASE_URL =
  process.env.REACT_APP_EJAGRITI_API_URL ||
  "https://court-data-service-596718606544.asia-south1.run.app";

const BASE = `${EJAGRITI_API_BASE_URL}/api/v1/ejagriti`;
const JSON_HEADERS = { "Content-Type": "application/json" };

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}

async function request(path, options) {
  const res = await fetch(`${BASE}${path}`, options);
  const body = await parseJsonSafe(res);
  if (!res.ok || !body || body.success === false) {
    const err = new Error((body && (body.message || body.error)) || "Request failed");
    err.code = body && body.errorCode;
    err.status = res.status;
    throw err;
  }
  return body;
}

const post = (path, data) =>
  request(path, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(data) });
const get = (path) => request(path, { method: "GET" });

/** GET /commissions/states -> [{ commissionId, commissionNameEn }] */
export async function getStates() {
  const body = await get("/commissions/states");
  return body.data || [];
}

/** GET /commissions/districts/:stateId -> [{ commissionId, commissionNameEn }] */
export async function getDistricts(stateId) {
  const body = await get(`/commissions/districts/${stateId}`);
  return body.data || [];
}

/**
 * Convert a direct e-Jagriti document URL (getDailyOrderJudgementPdf?...) into our
 * backend PDF-proxy URL. The portal returns the PDF as base64 inside JSON, so it
 * can't be opened directly; the proxy decodes it and serves real application/pdf.
 */
export function orderPdfProxyUrl(pdfUrl) {
  if (!pdfUrl) return null;
  try {
    const u = new URL(pdfUrl);
    const frn = u.searchParams.get("filingReferenceNumber");
    const doh = u.searchParams.get("dateOfHearing");
    const oti = u.searchParams.get("orderTypeId");
    if (frn && doh && oti) {
      return `${BASE}/pdf?filingReferenceNumber=${encodeURIComponent(frn)}&dateOfHearing=${encodeURIComponent(doh)}&orderTypeId=${encodeURIComponent(oti)}`;
    }
  } catch (_) {
    /* fall through */
  }
  return pdfUrl;
}

/** POST /case-status -> data { summary, actions, historyMetadata, historyAndOrders } */
export async function getCaseStatus(caseNumber, commissionId) {
  const payload = { caseNumber: (caseNumber || "").trim() };
  if (commissionId) payload.commissionId = Number(commissionId);
  const body = await post("/case-status", payload);
  return body.data;
}

/** POST /case-history -> data { caseDetails, timelineMetadata, timeline } */
export async function getCaseHistory(caseNumber) {
  const body = await post("/case-history", { caseNumber: (caseNumber || "").trim() });
  return body.data;
}

/**
 * POST /advanced-search -> data { searchCriteria, pagination, results, commissionInfo, ... }
 * The server returns the full result set; paginate client-side.
 */
export async function advancedSearch(params) {
  const body = await post("/advanced-search", params);
  return body.data;
}
